import { AutocompleteGroupedOption, useEventCallback } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';

interface UseKeyboardNavigationProps<T> {
  groupedOptions: T[] | AutocompleteGroupedOption<T>[];
  anchorEl: HTMLDivElement | null;
  getOptionProps: (params: {
    option: T;
    index: number;
  }) => React.HTMLAttributes<HTMLLIElement> & { key: string | number };
  groupBy?: (option: T) => string;
}

export function useKeyboardNavigation<T>({
  groupedOptions,
  anchorEl,
  getOptionProps,
  groupBy,
}: UseKeyboardNavigationProps<T>): {
  optionRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  handleOptionKeyDown: (
    event: React.KeyboardEvent<HTMLLIElement>,
    index: number,
  ) => void;
  handleInputKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
    open: boolean,
  ) => void;
} {
  const optionRefs = useRef<(HTMLElement | null)[]>([]);

  const getTotalOptionCount = useCallback((): number => {
    if (!groupBy) {
      return groupedOptions.length;
    }

    return (groupedOptions as AutocompleteGroupedOption<T>[]).reduce(
      (total, group) => total + group.options.length,
      0,
    );
  }, [groupedOptions, groupBy]);

  const getOptionAtIndex = (index: number): T | null => {
    if (!groupBy) {
      return (groupedOptions as T[])[index] || null;
    }

    const groups = groupedOptions as AutocompleteGroupedOption<T>[];
    let currentIndex = 0;

    for (const group of groups) {
      if (index < currentIndex + group.options.length) {
        return group.options[index - currentIndex];
      }
      currentIndex += group.options.length;
    }

    return null;
  };

  const focusOption = useEventCallback((index: number) => {
    const option = optionRefs.current[index];
    if (option) {
      option.focus();
    }
  });

  const focusInput = useEventCallback(() => {
    const input = anchorEl?.querySelector('input') as HTMLElement;
    input?.focus();
  });

  const handleOptionKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
      const totalOptions = getTotalOptionCount();

      switch (event.key) {
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const option = getOptionAtIndex(index);
          if (option) {
            const optionProps = getOptionProps({ option, index });
            optionProps.onClick?.(
              event as unknown as React.MouseEvent<HTMLLIElement>,
            );
          }
          break;
        }

        case 'ArrowDown':
          event.preventDefault();
          if (index < totalOptions - 1) {
            focusOption(index + 1);
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (index > 0) {
            focusOption(index - 1);
          } else {
            focusInput();
          }
          break;
      }
    },
  );

  const handleInputKeyDown = useEventCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, open: boolean) => {
      if (event.key === 'ArrowDown' && open && getTotalOptionCount() > 0) {
        event.preventDefault();
        focusOption(0);
      }
    },
  );

  useEffect(() => {
    const totalOptions = getTotalOptionCount();
    optionRefs.current = optionRefs.current.slice(0, totalOptions);
  }, [getTotalOptionCount]);

  return {
    optionRefs,
    handleOptionKeyDown,
    handleInputKeyDown,
  };
}
